import { DataTableFilter, DataTableFilterOption, FilterType } from './../controls/datatable/datatable.component';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { Activity, ActivityTask, ActivityTaskHost, ActivityTaskInstance, SolutionInfo } from 'src/app/model/SolutionInfo';
import { ActivityCapabilities, ActivityCapabilitiesRequest, ActivityCapabilitiesType, TaskStatus } from 'src/app/model/TaskStatus';
import { ActivityResponse, ActivityService } from 'src/app/service/activity.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalControl, ModalControlType, ModalMessageComponent, ModalMessageType, ModalResult } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { ActivitySystemTaskDetailsComponent } from '../dialogs/activity-system-task-details/activity-system-task-details.component';
import { ActivitySoftwareRollbackComponent } from '../dialogs/activity-software-rollback/activity-software-rollback.component';
import { AuditField } from '../settings-audit/settings-audit.component';
import { DataTableService } from 'src/app/service/data-table.service';
import { Tab, TabColor } from '../controls/tabs/tabs.component';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { BaseComponent } from 'src/app/model/BaseComponent';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { SolutionService } from 'src/app/service/solution.service';

enum ActivitiesDetailsState {INIT, VIEW_DETAILS}
enum ActivityPropertyTab {GENERAL, SUMMARY, SYSTEMS}
@Component({
    selector: 'app-activities-overview',
    templateUrl: './activities-overview.component.html',
    styleUrls: ['./activities-overview.component.css', '../activities/activities.component.css'],
    host: {'animate.enter': 'enter-animation'},
    standalone: false
})
export class ActivitiesOverviewComponent extends BaseComponent implements OnInit {

  title: string = $localize`All Environments`;

  TabColor = TabColor;
  ActivityPropertyTab = ActivityPropertyTab;
  activePropertyTab: ActivityPropertyTab = ActivityPropertyTab.GENERAL;
  generalTab = new Tab('activity-info-general-tab', $localize`General`, undefined, true);
  summaryTab = new Tab('activity-info-summary-tab', $localize`Summary`, undefined, false);
  systemsTab = new Tab('activity-info-systems-tab', $localize`Systems`, undefined, false);
  activityPropertyTabs: Tab[] = [this.generalTab, this.summaryTab, this.systemsTab];
  cols: Array<DataTableColumn>;
  loading: boolean = true;
  activityResponse: ActivityResponse;
  activities: DataTableRow[] = [];
  total: number = 0;
  ActivitiesDetailsState = ActivitiesDetailsState;
  detailState:ActivitiesDetailsState = ActivitiesDetailsState.INIT;

  currentActivity:Activity;

  activitiesTableFilter:DataTableFilter[] = [];
  filterOpen: boolean = false;
  filterApplied: boolean = false;
  currentActivitiesTableFilter:string = "";
  showRemoveAllActivities: boolean = false;

  //summary table
  activitySummaryCol: DataTableColumn[] = [];
  activitySummaryRow: DataTableRow[] = [];
  //systems table
  activitySystemsCol: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn("host", $localize`Host`, ColumnType.STRING, true, "20"),
    new DataTableColumn("path", $localize`Path`, ColumnType.STRING, false, "25"),
    new DataTableColumn("message", $localize`Message`, ColumnType.STRING, false, "35"),
    new DataTableColumn("status", $localize`Status`, ColumnType.STATUSEXT, false, "8"),
    new DataTableColumn("action", $localize`Actions`, ColumnType.LINK, false, "12"),);
  activitySystemsRow: DataTableRow[] = [];
  
  softwareRollbackCapabilities:ActivityCapabilities;
  
  eventMessageSubscription: Subscription;
  solutionInfoSubscription: Subscription;
  filterSubscription: Subscription;
  applyFilterSubscription: Subscription;
  cancelFilterSubscription: Subscription;

  solutionInfo: SolutionInfo;
  
  constructor(public activityService: ActivityService, public eventService: EventService, public router: Router,
              public dataSrv: DataService, public route: ActivatedRoute, protected modal: Modal, 
              public wsService: WebsocketService, private dataTableSrv: DataTableService, public siService: SoftwareInventoryService,
              protected authService: AuthenticationService, protected solutionService: SolutionService) {
                super(eventService, modal, authService, solutionService);
    this.cols = Array<DataTableColumn>(
      new DataTableColumn("id", "Id", ColumnType.STRING, false, "5"),
      new DataTableColumn("friendlyName", "Name", ColumnType.STRING, false, "20"),
      new DataTableColumn("systemInfo", "System", ColumnType.STRING, false, "20"),
      new DataTableColumn("envStr", "Environment", ColumnType.STRING, false, "15"),
      new DataTableColumn("statusCol", "Status", ColumnType.STATUSEXT, false, "6"),
      new DataTableColumn("initiatedBy", "Initiated by", ColumnType.STRING, false, "8"),
      new DataTableColumn("createdDateTime", "Created", ColumnType.DATEFROMNOW, false, "8"),
      new DataTableColumn("updatedDateTime", "Updated", ColumnType.DATEFROMNOW, true, "8", ColumnSort.DESC),
      new DataTableColumn("duration", "Duration", ColumnType.DURATION, false, "10")
    );
  }

  ngOnInit(): void {
    this.getActivities("", PageCount.TWENTYFIVE, 0);

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.getActivities("", PageCount.TWENTYFIVE, 0);
      if(this.solutionInfo !== undefined && 
        this.solutionInfo.currentEnvironment.id !== newSolutionInfo.currentEnvironment.id) {
        this.detailState = ActivitiesDetailsState.INIT;
      }
      this.solutionInfo = newSolutionInfo;
    });

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if(msg.eventType == Events.ActivityCapabilities){
        if(this.isPendingTransaction(msg.headers.transactionId, true)){
          this.softwareRollbackCapabilities = JSON.parse(msg.body.msg);

          if(this.softwareRollbackCapabilities.activityType == ActivityCapabilitiesType.SOFTWARE_ROLLBACK){
            if(this.softwareRollbackCapabilities.ok){
              const sub: Subscription =
                this.modal.open(ActivitySoftwareRollbackComponent, {
                  data: { capabilities: this.softwareRollbackCapabilities },
                  id: 'otsc-activity-software-rollback'
                }
                ).afterClosed.subscribe((result: boolean) => {
                  sub.unsubscribe();
                });
            }else if(!this.softwareRollbackCapabilities.ok && this.softwareRollbackCapabilities.error == 'CAPABILITIES_AGENTS_OFFLINE'){
              this.modal.open(ModalMessageComponent, {
                data: {
                        title: $localize`Error`,
                        message: $localize`The system was unable to retrieve the list of required tasks. Please make sure the required agents are online and try again.`,
                        type: ModalMessageType.Error,
                        primaryBtn: $localize`Close`,
                        width: '350px'
                      },
                id: 'otsc-activity-rollback-system-error-msg'
                }
              );
            }
          }
        }
      }else if(msg.eventType == Events.ActivityRefresh){
        if(this.currentActivity && msg.body && (this.currentActivity.id == msg.body.id)){
          this.activityService.getActivityById(this.currentActivity.id).pipe(
            tap(resp => {
              this.setCurrentActivity(resp.body, false);
            }),
            finalize(() => {
              this.loading = false;
            })).subscribe();
        }else{
          this.getActivities("", PageCount.TWENTYFIVE, 0);
        }
      }else if(msg.eventType == Events.ActivityTaskStatus){
        let data:any = msg.body

        if(this.currentActivity && this.currentActivity.id == data.activityId){
            this.currentActivity.hostTasks.forEach((host:ActivityTaskHost) => {
              if(host.macAddress == data.hostAddress){
                host.instances.forEach((task:ActivityTaskInstance) => {
                  if(data.instancePath && data.instancePath != ''){
                    if(task.path == data.instancePath){
                      let row:DataTableRow[] = this.activitySystemsRow.filter((row:DataTableRow) => {
                        return row.data.systemAddress == data.hostAddress && row.data.path == data.instancePath;
                      });
  
                      if(row.length > 0){
                        row[0].data.message = data.message ? data.message : '';
                      }
                    }
                  }else{
                    let rows:DataTableRow[] = this.activitySystemsRow.filter((row:DataTableRow) => {
                      return row.data.systemAddress == data.hostAddress;
                    });

                    if(rows.length > 0){
                      rows.forEach((row:DataTableRow) => {
                        row.data.message = data.message ? data.message : '';
                      });
                    }
                  }
                })
              }
            });
        }
      }
    });

    this.filterSubscription = this.dataTableSrv.filterClicked$.subscribe((isOpen) => {
      this.filterOpen = isOpen;
      if (!this.filterOpen && this.filterApplied) {
        this.getActivities("", PageCount.TWENTYFIVE, 0);
        this.filterApplied = false;
      }
    });

    this.applyFilterSubscription = this.dataTableSrv.applyFilter$.subscribe((appliedFilters: DataTableFilter[]) => {
      this.currentActivitiesTableFilter = appliedFilters[0].selected;
      this.getActivities(appliedFilters[0].selected, PageCount.TWENTYFIVE, 0);
      this.filterApplied = true;
    });

    this.cancelFilterSubscription = this.dataTableSrv.cancelFilter$.subscribe(() => {
      this.getActivities("", PageCount.TWENTYFIVE, 0);
      this.filterApplied = false;
    });
  }

  ngOnDestroy(){
    this.eventMessageSubscription.unsubscribe();
    this.solutionInfoSubscription.unsubscribe();
    this.filterSubscription.unsubscribe();
    this.applyFilterSubscription.unsubscribe();
    this.cancelFilterSubscription.unsubscribe();
  }

  getActivities(statusType: string, itemsPerPage:number, startingIndex:number): void {
    this.activityService.listActivity(statusType, itemsPerPage, startingIndex).pipe(
      tap(resp => {
        this.activityResponse = resp.body;
        this.total = this.activityResponse.numActivity;
        this.activities = this.formatActivities();
        this.activitiesTableFilter = this.createOtherActivitiesTableFilter();
        this.showRemoveAllActivities = this.activityResponse.numActivity > 0;
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  formatActivity(activity:Activity): DataTableRow {
    var instanceCount: number = 0;
    activity.friendlyName = Utils.getActivityTaskName(activity.name);
    activity.envStr = activity.env ? activity.env.solName + ' : ' + activity.env.name : '';

    if (activity.isTaskActivity) {
      activity.hostTasks.forEach(task => {
        instanceCount += task.instances.length;
      });
    }

    activity.instanceCount = instanceCount;
    activity.statusCol = Utils.getActivityStatus(activity.status);
    if (!activity.isTaskActivity) {
      activity.systemInfo = activity.system + " (" + activity.systemAddress + ")";
    } else {
      activity.systemInfo = this.getSystemColumnInfo(activity);
    }

    if (activity.initiatorFirstName == '' || activity.initiatorLastName == '') {
      activity.initiatedBy = activity.initiatorUserName;
    }

    if (activity.initiatorFirstName != '' && activity.initiatorLastName != '') {
      activity.initiatedBy = activity.initiatorFirstName + ' ' + activity.initiatorLastName;
    }

    if (activity.status === 'ERROR' && activity.isTaskActivity) {
      var isRollBackSupported = false;
      var failedTask: ActivityTask = this.getFailedTask(activity);
      var reqObj = JSON.parse(activity.args);

      if(failedTask){
        if (reqObj != null && reqObj.hasOwnProperty("body")) {
          if (reqObj.body != null && reqObj.body.hasOwnProperty("capabilities")) {
            isRollBackSupported = reqObj.body.capabilities.supportsRollback;
          }
        }

        if (this.isTaskSupported(activity.isSequential ? "supportedZeroDowntimeRollbackTasks" :
          "supportedRollbackTasks", failedTask.type, reqObj)) {
          activity.isRollback = isRollBackSupported && reqObj.body.isSupportHotfix;
        }
      }
    } else if (activity.status === 'ERROR' && activity.info) {
      for (var key in activity.info) {
        if (activity.info.hasOwnProperty(key)) {
          if (activity.info[key] && activity.info[key].currState &&
            (activity.info[key].currState === 'ACTIVITY_STATE_START_MASTER_SYSTEM_AND_REGISTERED_ADMIN_SERVERS') ||
            activity.info[key].currState === 'ACTIVITY_STATE_DB_UPGRADE' ||
            activity.info[key].currState === 'ACTIVITY_STATE_START_REMAINING_SERVICES') {
            activity.isRollback = true;
          }
        }
      }
    }

    let actions:ActionMenuItem[] = [new ActionMenuItem("Details", $localize`Details`, ActionMenuIcon.DETAILS, activity, false)];

    if(activity.name == 'PATCH_INSTALLATION' || activity.name == 'REMOVE_PATCHES' || activity.name == 'SYNC_PATCH_INSTALLATION' ||
      activity.name == 'BRING_UP_TO_DATE'){
      var patchInstallObj = JSON.parse(activity.args);
      activity.installTaskType = (patchInstallObj.body.installTaskType) ? patchInstallObj.body.installTaskType : activity.installTaskType = 0;
    }

    if((activity.name == 'PATCH_INSTALLATION' || activity.name == 'SYNC_PATCH_INSTALLATION') &&
      activity.status == 'ERROR' && activity.isRollback){
        actions.push(new ActionMenuItem("Rollback", $localize`Software rollback`, ActionMenuIcon.ROLLBACK, activity, false));
    }

    if (activity.status == 'QUEUED' || activity.status == 'RUNNING'){
      actions.push(new ActionMenuItem("Cancel", $localize`Cancel`, ActionMenuIcon.CANCEL, activity, false));
    }

    if(activity.status === 'ERROR' && activity.isRetryOnError){
      var reqObj = JSON.parse(activity.args);
      var failedTask: ActivityTask = this.getFailedTask(activity);

      if(this.isTaskSupported(activity.isSequential ? "supportedZeroDowntimeRetryPatchInstallationTasks" : "supportedRetryPatchInstallationTasks", failedTask.type, reqObj)){
        actions.push(new ActionMenuItem("Retry_On_Error", $localize`Retry`, ActionMenuIcon.EXECUTE, activity, false));
      }
    }

    return new DataTableRow(activity, actions);
  }

  formatActivities(): DataTableRow[] {
    let rows:DataTableRow[] = [];

    this.activityResponse.activities.forEach((activity) => {
      rows.push(this.formatActivity(activity));
    });

    return rows;
  }

  createOtherActivitiesTableFilter():DataTableFilter[] {
    let activityFilter: DataTableFilterOption[] = [];
    this.activityResponse.filters.forEach( filter => {
      switch (filter) {
        case 'CANCELED':
          activityFilter.push(new DataTableFilterOption($localize`Canceled`, filter));
          break;
        case 'COMPLETED':
          activityFilter.push(new DataTableFilterOption($localize`Completed`, filter));
          break;
        case 'QUEUED':
          activityFilter.push(new DataTableFilterOption($localize`Queued`, filter));
          break;
        case 'ERROR':
          activityFilter.push(new DataTableFilterOption($localize`Error`, filter));
          break;
        case 'RUNNING':
          activityFilter.push(new DataTableFilterOption($localize`Running`, filter));
          break;
        default:
          activityFilter.push(new DataTableFilterOption(filter, filter));
          break;
      }
    });
    activityFilter.unshift(new DataTableFilterOption($localize`All activities`, ""));
    activityFilter.sort((a, b) => (a.text > b.text ? 1 : a.text == b.text ? 0 : -1));

    return Array<DataTableFilter>(
      new DataTableFilter("other-activities-filter-status",
                          AuditField.TYPE,
                          $localize`Status`, 
                          FilterType.DROPDOWN, 
                          activityFilter)
    );
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    if(event.name === "Details"){
      this.detailState = ActivitiesDetailsState.VIEW_DETAILS;
      this.setCurrentActivity(event.data[0], true);
    }else if(event.name === "Retry_On_Error"){
      let activity:Activity = event.data[0];
      var failedTask: ActivityTask = this.getFailedTask(activity);
      var reqObj = JSON.parse(activity.args);

      //check if the task supports retry on error
      if (this.isTaskSupported( activity.isSequential ? "supportedZeroDowntimeRetryPatchInstallationTasks" : "supportedRetryPatchInstallationTasks", failedTask.type, reqObj)) {
        const sub: Subscription =
          this.modal.open(ModalMessageComponent, {
            data: {
              title: $localize`Retry patch installation activity`,
              message: $localize`<b>Warning</b>: Before retrying, inspect the logs and address any issues that may have caused the error. The activity will be restarted from the last task that encountered errors.<br/><b>Note</b>: This action does not verify the prerequisite requirements.`,
              type: ModalMessageType.Warning,
              primaryBtn: $localize`Retry`,
              secondaryBtn: $localize`Cancel`,
              width: '550px',
              disableButtonsOnClick: true          
            },
            id: 'otsc-activity-retry-task-dlg'
          }
          ).afterClosed.subscribe((result: boolean) => {
            if (result) {
              this.activityService.retryActivity(activity.id).pipe(
                tap(resp => {
                  let status:TaskStatus = resp.body;
                  let bannerType:Events = Events.ErrorMsg;
                  let bannerMsg:string;
        
                  if(status.completed && status.success){
                    bannerType = Events.InfoMsg;
                    bannerMsg = $localize`Retrying the ${Utils.getActivityTaskName(activity.name)} activity (Id: ${activity.id}).`;
                  }else{
                    switch (resp.body.errorCode) {
                      case 'RETRY_ACTIVITY_RETRY_ERROR':
                        bannerMsg = $localize`Error retrying the activity (Id: ${activity.id}).`;
                        break;
                      case 'RETRY_ACTIVITY_NO_PERM':
                        bannerMsg = $localize`Only the original initiator can retry the activity.`;
                        break;
                      case 'RETRY_ACTIVITY_NOT_FOUND':
                        bannerMsg = $localize`The activity (Id: ${activity.id}) is not found.`;
                        break;
                      case 'RETRY_ACTIVITY_NO_ENV_ACCESS':
                        bannerMsg = $localize`You do not have permission to access the activity’s environment.`;
                        break;
                      case 'RETRY_ACTIVITY_NO_TASK_ERROR':
                        bannerMsg = $localize`This activity cannot be retried because no task with an error was detected.`;
                        break;
                      case 'RETRY_ACTIVITY_INELIGIBLE':
                        bannerMsg = $localize`This activity (Id: ${activity.id}) has become ineligible for a retry.`;
                        break;
                      case 'RETRY_NEWER_ACTIVITY_EXIST':
                        bannerMsg = $localize`Activity retry not possible due to a newer existing activity.`;
                        break;
                      default:
                        bannerMsg = $localize`Error retrying the activity (Id: ${activity.id}). See the log for details.`;
                        break;
                    }
                  }

                  Utils.addHeaderMessageEventBanner(this.eventService, bannerType, bannerMsg);
                }),
                catchError(resp => {
                  return of(resp);
                })).subscribe();
            }
            sub.unsubscribe();
          });
      }
    }else if(event.name === "Rollback"){
      let requestData:any = JSON.parse(event.data[0].args);
      this.siService.sendActivityCapabilities(this.wsService.getClientId(), this.wsService.getWSEnvId(), this.addTransaction(),
        new ActivityCapabilitiesRequest(event.data[0].id, 'SOFTWARE_ROLLBACK', requestData.body.pluginId, '', '')).pipe(
      tap(resp => {
      }),
      catchError(resp => {
        Utils.addTaskErrorHeaderMessageEventBanner(this.eventService, "ACTIVITY_CAPABILITIES", undefined);
        return of(resp);
      })).subscribe();
    }else if(event.name === "Cancel"){
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Cancel activity`,
            message: $localize`Are you sure you want to cancel this activity? <br/> <b>Note:</b> This action will cancel any related activities.`,
            type: ModalMessageType.Question,
            primaryBtn: $localize`Yes`,
            secondaryBtn: $localize`No`,
            width: '450px'
          },
          id: 'otsc-activity-cancel-task-dlg'
        }
        ).afterClosed.subscribe((result: boolean) => {
          if (result) {
            let bannerMsg: MsgPayLoad;

            this.activityService.cancelActivity(event.data[0].id).pipe(
              tap(resp => {
                bannerMsg = new MsgPayLoad(undefined, {
                  message: $localize`The activity canceled successfully.`
                });
                bannerMsg.eventType = Events.SuccessMsg;
                this.getActivities("", PageCount.TWENTYFIVE, 0);
              }),
              catchError(resp => {
                if (resp.status === 400) {
                  bannerMsg = new MsgPayLoad(undefined, {
                    message: $localize`The activity was not canceled. Please try again later.`
                  });
                  bannerMsg.eventType = Events.WarningMsg;
                }
                return of(resp);
              }),
              finalize(() => {
                Utils.addHeaderMessageBanner(this.eventService, bannerMsg);
              }),
            ).subscribe();
          }
          sub.unsubscribe();
        });
    }
  }

  viewTaskErrorMessage(row:DataTableRow): void {
    this.modal.open(ModalMessageComponent, {
      data: {
              title: $localize`Task '${row.data.task}' details`,
              message: row.data.message,
              type: ModalMessageType.Error,
              primaryBtn: $localize`Close`,
              width: '450px'
            },
      id: 'otsc-activity-task-error-msg'
      }
    );
  }

  viewSystemsDetails(row:DataTableRow): void {
    const sub:Subscription = 
      this.modal.open(ActivitySystemTaskDetailsComponent, {
        data: {hostName: row.data.host, 
              path: row.data.path,
              hostAddress: row.data.systemAddress,
              activity: this.currentActivity,
              id: 'otsc-activity-system-task-detail-dialog'},
              width: '950px'
      }).afterClosed.subscribe((result:boolean) => {
        this.activityService.getActivityById(this.currentActivity.id)
        .pipe(
          tap(resp => {
            this.setCurrentActivity(resp.body, false);
          }),
          finalize(() => {
            this.loading = false;
          })).subscribe(() => {
            sub.unsubscribe();
          });
      });
  }

  goBack():void {
    this.getActivities("", PageCount.TWENTYFIVE, 0);
    this.detailState = ActivitiesDetailsState.INIT;
  }
  
  onSelectedPropertyTabChange(selectedIndex: number) {
    this.activePropertyTab = selectedIndex;
  }

  setActivityPackage(activity:Activity): void {
    let args:any = JSON.parse(activity.args);

    if(args.body){
      if (args.body.componentPatchesToInstall && args.body.componentPatchesToInstall.length > 0) {
        let compPatches: string[] = [];
        Object.keys(args.body.componentPatchesToInstall).forEach(key => {
          compPatches.push(args.body.componentPatchesToInstall[key].name);
        });
        activity.patchesToBeInstalled = compPatches.join(", ");
      } else {
        if(args.body.langPacksToInstall && args.body.langPacksToInstall.length > 0){
          activity.langPackToBeInstalled = args.body.langPacksToInstall.join(", ");
        }else{
          activity.langPackToBeInstalled = $localize`None`;
        }
    
        if(args.body.patchesToBeRemoved && args.body.patchesToBeRemoved.length > 0){
          activity.patchesToBeRemoved = args.body.patchesToBeRemoved.join(", ");
        }else{
          activity.patchesToBeRemoved = $localize`None`;
        }
    
        if(args.body.patchesToInstall && args.body.patchesToInstall.length > 0){
          activity.patchesToBeInstalled = args.body.patchesToInstall.join(", ");
        }else{
          activity.patchesToBeInstalled = $localize`None`;
        }

        if(args.body.patchesToReApply && args.body.patchesToReApply.length > 0){
          activity.patchesToReApply = args.body.patchesToReApply.join(", ");
        }else{
          activity.patchesToReApply = $localize`None`;
        }

        if(args.body.skipReapply == undefined) {
          activity.skipReapply = $localize`N/A`;
        } else {
          activity.skipReapplyValue = args.body.skipReapply;
          activity.skipReapply = args.body.skipReapply === true ? $localize`Yes` : $localize`No`;
        }
        
        if(args.body.updateName){
          activity.updateToBeInstalled = args.body.updateName;
        }else{
          activity.updateToBeInstalled = $localize`None`;
        }

        activity.langPackToReapply = (args.body.langPackToReapply !== undefined && args.body.langPackToReapply.length > 0) ?
          args.body.langPackToReapply.join(", ") : $localize`None`;

        activity.filesToReplace = (args.body.filesToReplaceFromUpdate == undefined) ? 0 : args.body.filesToReplaceFromUpdate;
      }
    }
  }

  setCurrentActivity(activity:Activity, isResetTab:boolean): void {
    this.currentActivity = activity;
    this.currentActivity.friendlyName = Utils.getActivityTaskName(activity.name);
    this.currentActivity.isRunning = this.currentActivity.status === 'RUNNING';
    this.formatActivity(this.currentActivity);
    this.activitySummaryRow = [];
    this.activitySystemsRow = [];
    this.systemsTab.show = this.currentActivity.isTaskActivity;

    if(isResetTab){
      setTimeout(() => {
        this.activePropertyTab = ActivityPropertyTab.GENERAL;
      }, 0);
    }

    if(activity.name == 'DISCOVER_EXTRA_INFO' || activity.name == 'DISCOVER'){
      if(this.activitySummaryCol.length == 0 || this.activitySummaryCol[0].field !== 'products'){
        this.activitySummaryCol = Array<DataTableColumn>(
          new DataTableColumn("products", $localize`Products`, ColumnType.STRING, true, "40"),
          new DataTableColumn("installDir", $localize`Installation Directory`, ColumnType.STRING, false, "40"),
          new DataTableColumn("result", $localize`Result`, ColumnType.STRING, false, "20"));
      }

      if(activity.status == 'COMPLETED'){
        let response: any = JSON.parse(this.currentActivity.response);
        
        response.forEach((productInfo:any) => {
          let result:string = "";

          if(productInfo.flag === "added"){
            result = $localize`Added`;
          }else if(productInfo.flag == "unchanged"){
            result = $localize`Unchanged`;
          }else if(productInfo.flag == "removed"){
            result = $localize`Removed`;
          }
  
          this.activitySummaryRow.push(new DataTableRow(
                                        {products: productInfo.productName + ' ' + productInfo.productVersion,
                                         installDir: productInfo.installationDirectory, 
                                         result: result}, []));
        });
      }
    }else {
      if(this.activitySummaryCol.length == 0 || this.activitySummaryCol[0].field !== 'task'){
        this.activitySummaryCol = Array<DataTableColumn>(
          new DataTableColumn("task", $localize`Task`, ColumnType.STRING, true, "35"),
          new DataTableColumn("message", $localize`Message`, ColumnType.STRING, false, "40"),
          new DataTableColumn("status", $localize`Status`, ColumnType.STATUSEXT, false, "10"),
          new DataTableColumn("action", $localize`Actions`, ColumnType.LINK, false, "15"),);
      }
      this.setActivityPackage(this.currentActivity);

      if(this.currentActivity.isTaskActivity){
        let instanceErrorCount: number = 0;
        this.currentActivity.taskList.forEach((globalTask:ActivityTask) => {
          globalTask.completedCount = 0;
          globalTask.taskName = ActivitySystemTaskDetailsComponent.getTaskName(globalTask.type);
          let message:string = "";

          if(globalTask.isAgentTask){
            this.currentActivity.hostTasks.forEach((host:ActivityTaskHost) => {
              host.instances.forEach((instance:ActivityTaskInstance) => {
                instanceErrorCount = 0;
                instance.result = new TaskStatus();
                instance.result.success = true;
                instance.result.completed = true;
                let lastestResult:TaskStatus[] = [];
    
                instance.tasks.forEach((task:ActivityTask) => {
                  if(task.isAgentTask){
                    if(task.type == globalTask.type && task.completed){
                      globalTask.completedCount++;
                    }
      
                    if(!task.completed){
                      instance.result.completed = false;
                    }
                    
                    if(!task.success){

                      instance.result.success = false;
                      lastestResult.push(task);
                    }

                    if (task.completed && !task.success) {
                      instanceErrorCount++;
                    }
                    task.taskName = ActivitySystemTaskDetailsComponent.getTaskName(task.type);
                  }
                });
    
                let existingSystem = this.activitySystemsRow.filter((row:DataTableRow) => {
                                                              return row.data.host === host.name && row.data.path === instance.path;
                                                            });
                
                if(existingSystem.length < 1){
                  this.activitySystemsRow.push((new DataTableRow({host: host.name, path: instance.path, 
                                                                  status: ActivitySystemTaskDetailsComponent.getTaskStatus(lastestResult.length > 0 ? lastestResult[0]: instance.result, this.currentActivity.isRunning),
                                                                  instanceData: instance,
                                                                  message: lastestResult.length > 0 && !lastestResult[0].success ? lastestResult[0].errorMessage :'',
                                                                  systemAddress: host.macAddress,
                                                                  action: $localize`View details`
                                                                },[])));
                }
              });
            });
          } else {
            //we need to track non agent tasks separately not as part of the instance
            if(globalTask.completed){
              globalTask.completedCount++;
            }
          }
          
          if (this.currentActivity.isRunning || this.currentActivity.isSequential || (globalTask.completed && globalTask.success)) {
            if (this.currentActivity.isSequential && globalTask.completed && !globalTask.success) {
              message = globalTask.isAgentTask ? $localize`${globalTask.completedCount - instanceErrorCount} out of ${this.currentActivity.instanceCount} completed` :
                $localize`${globalTask.completedCount} out of 1 completed`;
              message += " (" + globalTask.errorMessage + ")";
            } else {
              message = globalTask.isAgentTask ? $localize`${globalTask.completedCount} out of ${this.currentActivity.instanceCount} completed` :
                $localize`${globalTask.completedCount} out of 1 completed`;
            }
          } else if (globalTask.completed && !globalTask.success) {
            message = globalTask.errorMessage;
          }

          this.activitySummaryRow.push(new DataTableRow({task: globalTask.taskName,
                                                         message: message, 
                                                          status: ActivitySystemTaskDetailsComponent.getTaskStatus(globalTask, this.currentActivity.isRunning),
                                                          action: globalTask.completed && !globalTask.success ? $localize`View errors` : ''}, []));
        });
  
        
      }
    }
  }

  getSystemColumnInfo(activity: Activity) {
    var columnInfo = '';
    columnInfo += activity.hostTasks.length + ' ';

    if (activity.hostTasks.length > 1) {
      columnInfo += 'Systems';
    } else {
      columnInfo += 'System';
    }

    columnInfo += " (" + activity.instanceCount + " ";

    if (activity.instanceCount > 1) {
      columnInfo += 'Instances';
    } else {
      columnInfo += 'Instance';
    }

    columnInfo += ")";

    return columnInfo;
  }

  getFailedTask(activity: Activity): ActivityTask {
    var failedTask: ActivityTask;
    activity.taskList.forEach(task => {
      if (task.completed && !task.success) {
        failedTask = task;
        return;
      }
    });
    return failedTask;
  }

  isTaskSupported(propertyName: string, failedTaskType: string, reqObj: any): boolean {
    var isSupported: boolean = false;

    if (reqObj != null && reqObj.body != null &&
      reqObj.body.hasOwnProperty("capabilities") &&
      reqObj.body.capabilities.hasOwnProperty(propertyName)) {
      reqObj.body.capabilities[propertyName].forEach(task => {
        if (failedTaskType === task.type) {
          isSupported = true;
          return;
        }
      });
    }

    return isSupported;
  }

  onPageChange(pageEvent: PageEvent) {
    this.getActivities(this.filterApplied ? this.currentActivitiesTableFilter : "", pageEvent.itemsPerPage, pageEvent.startingIndex);
  }

  activitiesRowClick(event: any): void{
    this.detailState = ActivitiesDetailsState.VIEW_DETAILS;
    this.setCurrentActivity(event.data, true);
  }

  removeAllActivitiesTable(event: any): void {
    let msg:string = $localize`You are about to remove all activities with the exception of 'Queued' or 'Running' items. Do you wish to proceed?`;
    let checkControl: ModalControl = new ModalControl('include-package-logs', ModalControlType.CHECKBOX,
    $localize`Include log packages`, false);
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Remove`,
          message: msg,
          type: ModalMessageType.Question,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '425px',
          useResult: true,
          controls: [checkControl]
        },
        id: 'otsc-removeall-activities-dialog'
      }).afterClosed.subscribe((result: ModalResult) => {
        if (result) {
          let bannerMsg: MsgPayLoad;
          this.activityService.removeAllActivities(result.controls[0].val).pipe(
            tap(resp => {
              bannerMsg = new MsgPayLoad(undefined, {
                message: $localize`Activities were removed successfully.`
              });

              bannerMsg.eventType = Events.SuccessMsg;
              this.getActivities("", PageCount.TWENTYFIVE, 0);
            }),
            catchError(resp => {
              bannerMsg = new MsgPayLoad(undefined, {
                message: $localize`Error removing activities. Please try again later.`
              });

              bannerMsg.eventType = Events.ErrorMsg;
              return of(resp);
            }),
            finalize(() => {
              Utils.addHeaderMessageBanner(this.eventService, bannerMsg);
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      }
      );
  }

  viewDetails() {
    switch(this.currentActivity.name){
      case 'PACKAGE_PROD_LOGS':
        this.router.navigate(['/products/logsPackageDownloads']);
        break;
    }
  }

  onActivityListKeyDown(event: KeyboardEvent, row: DataTableRow, index: number): void {
    let activityElements:NodeListOf<Element> = document.getElementById('activityOverviewList').querySelectorAll('.ot-list__item');
    switch(event.key){
      case "ArrowUp":
        if((index - 1) > -1){
          (activityElements[index - 1] as HTMLLIElement).focus();
        }
      break;
      case "ArrowDown":
        if((index + 1) < activityElements.length){
          (activityElements[index + 1] as HTMLLIElement).focus();
        }
        break;
    }
  }
}